/**
 * Google Tag Manager snippets. Load GTM only when `shouldLoadAnalytics(env, id)` is true
 * (production with a container id). Never on test hosts.
 */

export interface GtmScriptOptions {
  /** CSP nonce, added to the injected gtm.js script. */
  nonce?: string;
  /** Defaults to "dataLayer". */
  dataLayerName?: string;
}

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const JS_IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

export function isValidGtmId(id: string): boolean {
  return GTM_ID_PATTERN.test(id);
}

/**
 * The standard GTM loader for the `<head>` (without the `<script>` tag, for inline injection).
 * Throws on a malformed id or data layer name so nothing user-controlled ends up in the script.
 */
export function gtmHeadScript(id: string, options: GtmScriptOptions = {}): string {
  assertGtmId(id);
  const dataLayerName = options.dataLayerName ?? "dataLayer";
  if (!JS_IDENTIFIER.test(dataLayerName)) throw new Error(`Invalid dataLayer name: ${dataLayerName}`);
  const nonceLine = options.nonce
    ? `var n=d.querySelector('[nonce]');n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));`
    : "";
  return (
    "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});" +
    "var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';" +
    `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;${nonceLine}f.parentNode.insertBefore(j,f);` +
    `})(window,document,'script','${dataLayerName}','${id}');`
  );
}

/** The `<noscript>` iframe for the start of `<body>`, as an HTML string including the noscript tag. */
export function gtmNoscriptHtml(id: string): string {
  assertGtmId(id);
  return `<noscript><iframe src="${gtmNoscriptSrc(id)}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`;
}

/** URL of the noscript iframe. */
export function gtmNoscriptSrc(id: string): string {
  assertGtmId(id);
  return `https://www.googletagmanager.com/ns.html?id=${id}`;
}

function assertGtmId(id: string): void {
  if (!isValidGtmId(id)) throw new Error(`Invalid Google Tag Manager id: ${id}`);
}
